<!-- src/routes/Dashboard.svelte -->
<script>
  import { onMount } from 'svelte'
  import { agentStore } from '$lib/stores'
  import GroupCard from '$components/GroupCard.svelte'
  import RunsPanel from '$components/RunsPanel.svelte'
  import StatsPanel from '$components/StatsPanel.svelte'
  import SettingsPanel from '$components/SettingsPanel.svelte'
  import LeadsPanel from '$components/LeadsPanel.svelte'
  import { API_BASE } from '$lib/api'

  export let view = 'dashboard'

  let groups = []
  let runs = []
  let providers = []
  let health = null
  let loading = true
  let error = null

  onMount(async () => {
    try {
      const [healthRes, groupsRes, providersRes, runsRes] = await Promise.all([
        fetch(`${API_BASE}/api/health`),
        fetch(`${API_BASE}/api/groups`),
        fetch(`${API_BASE}/api/providers`),
        fetch(`${API_BASE}/api/agents/runs?limit=20`),
      ])

      health = await healthRes.json()
      const groupsData = await groupsRes.json()
      const providersData = await providersRes.json()
      const runsData = await runsRes.json()

      groups = groupsData.groups || []
      providers = providersData.providers || []
      runs = runsData.runs || []

      agentStore.update(s => ({ ...s, groups, providers, runs, health }))
    } catch (err) {
      error = err.message
      console.error('Failed to load dashboard data:', err)
    } finally {
      loading = false
    }
  })

  $: activeRuns = runs.filter(r => r.status === 'running')
  $: recentRuns = runs.slice(0, 10)
</script>

{#if loading}
  <div class="loading">
    <div class="spinner"></div>
    <p>Loading GREA Desktop...</p>
  </div>
{:else if error}
  <div class="error">
    <h2>Connection Error</h2>
    <p>{error}</p>
    <p class="hint">Make sure the Cloudflare Worker is deployed at {API_BASE}</p>
  </div>
{:else}
  {#if view === 'dashboard'}
    <div class="dashboard">
      <StatsPanel {health} {providers} />
      <section class="groups-section">
        <h2 class="section-title">Grea Office Groups</h2>
        <div class="groups-grid grid grid-3">
          {#each groups as group}
            <GroupCard {group} />
          {/each}
        </div>
      </section>
      <RunsPanel {runs} />
    </div>
  {:else if view === 'settings'}
    <SettingsPanel />
  {:else if view === 'leads'}
    <LeadsPanel />
  {:else if view === 'agents'}
    <RunsPanel {runs} fullView />
  {/if}
{/if}

<style>
  .dashboard {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 1600px;
    margin: 0 auto;
  }
  .loading, .error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 60vh;
    gap: 16px;
    color: var(--text-muted);
  }
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .error h2 { color: var(--danger); }
  .error .hint { font-size: 12px; opacity: 0.7; }
  .groups-section { display: flex; flex-direction: column; gap: 16px; }
  .section-title { font-size: 18px; font-weight: 600; color: var(--text-primary); letter-spacing: 0.5px; }
  .groups-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
</style>
